import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Ability, subject } from '@casl/ability';
import { ProjectsService } from '../../../../../../core/projects.service';
import { projectDetails } from '../../../../../../core/projects.selectors';
import { appDataDetails } from '../../../../../../../../shared/core/shared.selectors';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { AppService } from '../../../../../../../../shared/services/app.service';
import { AuthService } from '../../../../../../../../shared/services/auth.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { RiskRegisterService } from '../risk-register.service';
import { Risk, RiskUpdate } from '../risk-register.models';
import { RESPONSE_TYPE_LABEL, STATUS_MAP, initials, ownerName, serverErrorMessage } from '../risk-register.util';
import { RiskFormComponent } from '../risk-form/risk-form.component';
import { RiskCommentDialogComponent } from '../risk-comment-dialog/risk-comment-dialog.component';
import { RiskConvertDialogComponent } from '../risk-convert-dialog/risk-convert-dialog.component';

const DIALOG = { panelClass: 'floating-dialog-box', maxHeight: '90vh', width: '460px' };
const UPDATES_PAGE = 20;
const escapeHtml = (v: string): string => String(v ?? '').replace(/[&<>"']/g, c => `&#${c.charCodeAt(0)};`);
const ACTION_DONE: Record<'approve' | 'reject' | 'close' | 'reopen', string> = {
  approve: 'approved', reject: 'rejected', close: 'closed', reopen: 'reopened',
};

/** Right panel for one risk (spec §5.4): Details + Updates tabs, owner/manager actions. */
@Component({
  selector: 'app-risk-detail-panel',
  templateUrl: './risk-detail-panel.component.html',
  styleUrls: ['./risk-detail-panel.component.scss'],
  standalone: false,
})
export class RiskDetailPanelComponent implements OnInit, OnDestroy {
  @Input() data: { riskId: string; projectId: string } = { riskId: null, projectId: null };
  @Input() options: Record<string, unknown> = {};

  risk: Risk | null = null;
  updates: RiskUpdate[] = [];
  updatesTotal = 0;
  loading = true;
  tabIndex = 0;
  showUpdateForm = false;
  projectTimezone = 'UTC';
  statusMap = STATUS_MAP;
  responseLabel = RESPONSE_TYPE_LABEL;
  canManage = false;
  canUpdateOwn = false;
  canApprove = false;
  canClose = false;
  canReopen = false;
  canConvert = false;
  canDelete = false;
  private myId: string;
  private readonly onDestroy = new Subject<void>();

  appService = inject(AppService);
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private projectsService = inject(ProjectsService);
  private commonService = inject(CommonService);
  private authService = inject(AuthService);
  private riskService = inject(RiskRegisterService);
  private ability = inject(Ability);

  ngOnInit(): void {
    this.myId = this.authService.getCurrentUser()?.id;
    this.refreshAbilities();
    this.store.select(appDataDetails).pipe(takeUntil(this.onDestroy)).subscribe(() => this.refreshAbilities());
    this.store.select(projectDetails).pipe(takeUntil(this.onDestroy))
      .subscribe((p: { timezone?: string } | null) => { if (p?.timezone) this.projectTimezone = p.timezone; });
    this.load();
  }

  private refreshAbilities(): void {
    const s = subject('Risk', { project_id: this.data.projectId });
    this.canManage = this.ability.can('update', s);
    this.canUpdateOwn = this.ability.can('update_own', s);
    this.canApprove = this.ability.can('approve', s);
    this.canClose = this.ability.can('close', s);
    this.canReopen = this.ability.can('reopen', s);
    this.canConvert = this.ability.can('convert', s);
    this.canDelete = this.ability.can('delete', s);
  }

  load(): void {
    this.loading = true;
    this.riskService.get(this.data.riskId).pipe(takeUntil(this.onDestroy)).subscribe({
      next: risk => { this.risk = risk; this.loading = false; this.loadUpdates(true); },
      error: err => { this.loading = false; this.commonService.notification(serverErrorMessage(err?.error, 'Could not load the risk'), 'error'); },
    });
  }

  loadUpdates(reset = false): void {
    const start = reset ? 0 : this.updates.length;
    this.riskService.updates(this.data.riskId, start, UPDATES_PAGE).pipe(takeUntil(this.onDestroy)).subscribe({
      next: resp => {
        this.updates = reset ? (resp?.data || []) : [...this.updates, ...(resp?.data || [])];
        this.updatesTotal = resp?.meta?.totalRecords || this.updates.length;
      },
      error: err => this.commonService.notification(serverErrorMessage(err?.error, 'Could not load the updates'), 'error'),
    });
  }

  get isOwner(): boolean { return !!this.risk?.owner && this.risk.owner.id === this.myId; }
  /** Add update: open risks only, for the owner (update_own) or a manager. */
  get canUpdate(): boolean { return this.risk?.status === 'open' && ((this.isOwner && this.canUpdateOwn) || this.canManage); }
  get canEdit(): boolean { return this.canManage && !!this.risk && this.risk.status !== 'converted'; }
  /** Pending proposal and the viewer may approve: Reject / Approve live in the footer. */
  get showApproveActions(): boolean { return this.risk?.status === 'pending' && this.canApprove; }
  get hasMenu(): boolean { return !!this.risk && (this.canManage || this.canDelete); }
  get title(): string { return this.risk ? `Risk ${this.risk.risk_no}` : 'Risk'; }
  owner(): string { return this.risk ? ownerName(this.risk) : ''; }
  ownerInitials(): string { return initials(this.risk?.owner); }

  /** The Updates tab is showing the add-update form (its own Cancel / Save replace the footer). */
  get composingUpdate(): boolean { return this.showUpdateForm && this.canUpdate && this.tabIndex === 1; }

  openUpdateForm(): void { this.showUpdateForm = true; this.tabIndex = 1; }

  onUpdateSaved(): void { this.showUpdateForm = false; this.tabIndex = 1; this.load(); this.riskService.refresh$.next(); }

  edit(): void {
    this.projectsService.openRightPanel.next({ component: RiskFormComponent, data: { projectId: this.data.projectId, mode: 'edit', risk: this.risk } });
  }

  approve(): void {
    this.dialog.open(RiskCommentDialogComponent, { ...DIALOG, data: { title: `Approve risk ${this.risk.risk_no}`, label: 'Comment (optional)', confirm: 'Approve', required: false } })
      .afterClosed().subscribe(res => { if (res !== undefined) this.run('approve', res?.comment); });
  }
  reject(): void {
    this.dialog.open(RiskCommentDialogComponent, { ...DIALOG, data: { title: `Reject risk ${this.risk.risk_no}`, label: 'Reason', confirm: 'Reject', required: true, destructive: true } })
      .afterClosed().subscribe(res => { if (res?.comment) this.run('reject', res.comment); });
  }
  close(): void {
    this.dialog.open(RiskCommentDialogComponent, { ...DIALOG, data: { title: `Close risk ${this.risk.risk_no}`, subtitle: this.risk.description, label: 'Reason', confirm: 'Close risk', required: true } })
      .afterClosed().subscribe(res => { if (res?.comment) this.run('close', res.comment); });
  }
  reopen(): void {
    this.dialog.open(RiskCommentDialogComponent, { ...DIALOG, data: { title: `Reopen risk ${this.risk.risk_no}`, label: 'Comment', confirm: 'Reopen', required: true } })
      .afterClosed().subscribe(res => { if (res?.comment) this.run('reopen', res.comment); });
  }
  private run(action: 'approve' | 'reject' | 'close' | 'reopen', comment?: string): void {
    this.riskService.action(this.risk.id, action, comment).pipe(takeUntil(this.onDestroy)).subscribe({
      next: risk => {
        this.risk = risk;
        this.showUpdateForm = false;
        this.loadUpdates(true);
        this.riskService.refresh$.next();
        this.commonService.notification(`Risk ${ACTION_DONE[action]}`, 'success');
      },
      error: err => this.commonService.notification(serverErrorMessage(err?.error, 'Action failed'), 'error'),
    });
  }

  toggleInternal(): void {
    this.riskService.update(this.risk.id, { internal: !this.risk.internal }).pipe(takeUntil(this.onDestroy)).subscribe({
      next: risk => { this.risk = risk; this.loadUpdates(true); this.riskService.refresh$.next(); },
      error: err => this.commonService.notification(serverErrorMessage(err?.error, 'Could not change visibility'), 'error'),
    });
  }

  convert(): void {
    this.dialog.open(RiskConvertDialogComponent, { ...DIALOG, width: '520px', data: { risk: this.risk, projectId: this.data.projectId } })
      .afterClosed().subscribe((risk: Risk | undefined) => {
        if (risk) { this.risk = risk; this.showUpdateForm = false; this.loadUpdates(true); this.riskService.refresh$.next(); }
      });
  }

  remove(): void {
    this.dialog.open(ConfirmDialogComponent, {
      disableClose: true, panelClass: 'floating-dialog-box', maxHeight: '90vh',
      data: new ConfirmDialogModel('Delete risk', `Delete risk <b>${escapeHtml(this.risk.risk_no)}</b> and its history? This cannot be undone.`, 'Delete', 'Cancel', true),
    }).afterClosed().subscribe(ok => {
      if (!ok) return;
      this.riskService.delete(this.risk.id).pipe(takeUntil(this.onDestroy)).subscribe({
        next: () => { this.commonService.notification('Risk deleted', 'success'); this.riskService.refresh$.next(); this.closePanel(); },
        error: err => this.commonService.notification(serverErrorMessage(err?.error, 'Delete failed'), 'error'),
      });
    });
  }

  /** The wrapper's close handler also drops ?riskId= (projects-wrapper closeRightPanel). */
  closePanel(): void {
    this.projectsService.closeRightPanel.next();
  }

  ngOnDestroy(): void { this.onDestroy.next(); this.onDestroy.complete(); }
}
